import React, {useEffect, useState} from 'react'
import {useNavigate, useParams} from "react-router-dom";
import {getWaiverDetails} from "@/services/ticket/getWaiverDetails";
import {WaiverItem} from "@/types/event/WaiverItem";
import {Checkbox} from "@/components/ui/checkbox";
import {cn} from "@/lib/utils";
import {Button} from "@/components/ui/button";
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from "@/components/ui/card";
import {Alert, AlertDescription} from "@/components/ui/alert";
import {Badge} from "@/components/ui/badge";
import TriEventsLoadingScreen from "@/components/common/TriEventsLoadingScreen";
import {toast} from "sonner";
import {CheckCircle2} from "lucide-react";

const TodoList = () => {

    const { uuid } = useParams() as {uuid: string};

    const [waiverItems, setWaiverItems] = useState<WaiverItem[]>([]);
    const [completedItems, setCompletedItems] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    
    const navigate = useNavigate();

    const markLinkDone = (itemId: string) => {
        if (!completedItems.includes(itemId)) {
            setCompletedItems([...completedItems, itemId]);
        }
    }

    const toggleCheckbox = (itemId: string, checked: boolean) => {
        setCompletedItems(prev => checked ? [...prev, itemId] : prev.filter(x => x !== itemId));
    }

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const response  = await fetch(`${process.env.REACT_APP_API_URL}/tickets/update-waiver-items/${uuid}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    completed_items: completedItems,
                }),
            });
            if (!response.ok) {
                toast.error("There was an error with your submission, please try again");
            }
            else {
                toast.success("Submission received!");
            }
        }
        catch (e) {
            toast.error("There was an error with your submission, please try again");
        }
        setSubmitting(false);
    }

    // TODO: Replace with react query
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const items = await getWaiverDetails(uuid);
                setWaiverItems(items.waiver_items);
                setCompletedItems(items.completed_items);
            }
            catch (error) {
                console.error('Error fetching data:', error);
                navigate('/error', { state: { errorCode: error instanceof Error ? error.message : 'Unknown error' } });
            }
            setLoading(false);
        }
        fetchData();
    }, [uuid, navigate]);


    if (loading) {
        return <TriEventsLoadingScreen/>
    }

    if (waiverItems.length === 0) {
        return (
            <section className="bg-background min-h-screen">
                <div className="container mx-auto px-4 py-8">
                    <Card className="max-w-md mx-auto">
                        <CardContent className="p-8 text-center">
                            <h1 className="text-2xl font-semibold mb-2">No Todo Items Found</h1>
                            <p className="text-muted-foreground">There are no required items to complete for this ticket.</p>
                        </CardContent>
                    </Card>
                </div>
            </section>
        )
    }

    const completedCount = completedItems.length;
    const totalCount = waiverItems.length;
    const isAllCompleted = completedCount === totalCount;

    return (
        <section className="bg-background min-h-screen">
            <div className="container mx-auto px-4 py-8 md:py-16">
                <div className="max-w-2xl mx-auto space-y-6">
                    {/* Header Card */}
                    <Card className="border-none bg-[#23d6e4] text-white">
                        <CardContent className="p-6">
                            <div className="text-center">
                                <CardTitle className="text-3xl font-bold mb-2 text-white">
                                    Complete Required Items
                                </CardTitle>
                                <CardDescription className="text-white/80">
                                    Please complete all required items before attending the event
                                </CardDescription>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Progress Card */}
                    <Card>
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <h3 className="text-lg font-semibold">Progress</h3>
                                    <p className="text-sm text-muted-foreground">
                                        {completedCount} of {totalCount} items completed
                                    </p>
                                </div>
                                <Badge variant={isAllCompleted ? "default" : "secondary"}>
                                    {Math.round((completedCount / totalCount) * 100)}%
                                </Badge>
                            </div>
                            <div className="w-full bg-muted rounded-full h-2">
                                <div 
                                    className="bg-primary h-2 rounded-full transition-all duration-300" 
                                    style={{ width: `${(completedCount / totalCount) * 100}%` }}
                                ></div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Todo Items */}
                    <div className="space-y-4">
                        {waiverItems.map((item) => (
                            <FormItem 
                                key={item.uuid} 
                                waiverItem={item} 
                                linkClickedFn={() => markLinkDone(item.uuid)} 
                                checkboxClickedFn={(checked: boolean) => toggleCheckbox(item.uuid, checked)}
                                checked={completedItems.includes(item.uuid)} 
                            />
                        ))}
                    </div>

                    {/* Submit Button */}
                    <Card>
                        <CardContent className="p-6">
                            <div className="text-center">
                                <Button 
                                    onClick={handleSubmit} 
                                    disabled={!isAllCompleted || submitting}
                                    className="w-full md:w-auto bg-black text-white hover:bg-black/90 hover:text-white transition-transform duration-200 hover:scale-105"
                                    size="lg"
                                >
                                    {submitting ? (
                                        <>
                                            <span className="animate-spin mr-2">⏳</span>
                                            Submitting...
                                        </>
                                    ) : (
                                        <>
                                            <CheckCircle2 className="mr-2 h-4 w-4" />
                                            Submit All Items
                                        </>
                                    )}
                                </Button>
                                {!isAllCompleted && (
                                    <p className="text-sm text-muted-foreground mt-2">
                                        Please complete all items before submitting
                                    </p>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </section>
    )
}

interface FormItemProps {
    waiverItem: WaiverItem;
    checked: boolean;
    linkClickedFn?: () => void;
    checkboxClickedFn?: (checked: boolean) => void;
}

const FormItem: React.FC<FormItemProps> = (props: FormItemProps) => {
    const isCompleted = props.checked;
    
    return (
        <Card className={cn(
            "transition-all duration-200 hover:shadow-md",
            isCompleted && "border-green-200 bg-green-50/50"
        )}>
            <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 mt-1">
                        <Checkbox 
                            id={props.waiverItem.uuid} 
                            onCheckedChange={props.checkboxClickedFn} 
                            checked={props.checked} 
                            disabled={props.waiverItem.type === "link"}
                            className="h-5 w-5 border-2 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                        />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                        {props.waiverItem.type === 'checkbox' && (
                            <div className="flex items-center space-x-2">
                                <p className="text-lg font-normal">{props.waiverItem.message}</p>
                                {isCompleted && (
                                    <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
                                )}
                            </div>
                        )}
                        
                        {props.waiverItem.type === 'link' && (
                            <div className="flex items-center space-x-2">
                                <a 
                                    onClick={props.linkClickedFn} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    href={props.waiverItem.link}
                                    className="text-lg font-normal text-blue-600 hover:text-blue-800 underline transition-colors"
                                >
                                    {props.waiverItem.message}
                                </a>
                                {isCompleted && (
                                    <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
                                )}
                            </div>
                        )}
                        
                        {props.waiverItem.type === 'link' && (
                            <p className="text-sm text-muted-foreground mt-1">
                                Click the link above to review the content. The item will be automatically marked as completed.
                            </p>
                        )}
                    </div>
                </div>
            </CardContent>
        </Card>
    )
}

export default TodoList
